import {FC, ReactNode} from 'react'
import {ProcedureContext, ReportFormProps} from './ReportForm'
import {Controller, FormProvider, useForm, SubmitHandler} from 'react-hook-form'
import {FormErrors} from 'components/reactHookForm/FormErrors'
import {Panel, Body, Head} from 'components/ui/Panel'
import {Label} from 'components/forms/Label'
import styles from './R9Report.scss'
import {Checkbox} from 'jbc-front/components/presenters/form/Checkbox'
import {gql} from '@apollo/client'
import {useMutation, useQuery} from 'hooks/graphql'
import {EmployeeName} from 'pages/withAuth/adminPage/procedures/parts/EmployeeName'
import {yup} from 'libs/yup'
import {yupResolver} from '@hookform/resolvers/yup'
import {Textarea} from 'jbc-front/components/presenters/form/Textarea'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import {laborConsultantSchema, LaborConsultantSection} from './LaborConsultant'
import {dateFormat} from 'libs/formatter'
import {useConfirmUpgrade} from './useConfirmUpgrade'
import Button from 'jbc-front/components/Button'
import {useDispatch} from 'react-redux'
import {asyncError} from 'store/actions/asyncError'

// @ts-ignore
import {titleMap} from 'procedureStatuses/Show'

const UPDATE_R9_PARAM_SET = gql`
  mutation UpdateR9ParamSet($input: UpdateR9ParamSetInput!) {
    updateR9ParamSet(input: $input) {
      r9ParamSet {
        id
      }
    }
  }
`

interface UpdateR9ParamSetQueryVariables {
  input: {
    procedureStatusId: string
    attributes: {
      employeeSelected?: boolean | null
      canNotUseMyNumberInsurance?: boolean | null
      notes?: string | null
      isUseMyNumber?: boolean | null
      needLaborConsultant?: boolean | null
      laborConsultantCreateDate?: string | null
      laborConsultantName?: string | null
      laborConsultantAgentType?: 'submission_agent' | 'office_agent' | null
      laborConsultantPrefectureId?: string | null
      laborConsultantPhoneNumber0?: string | null
      laborConsultantPhoneNumber1?: string | null
      laborConsultantPhoneNumber2?: string | null
      version?: number
      r9DependentParamSets:
        | {
            employeeDependentId: string
          }[]
        | null
    }
  }
}

const UPDATE_PROCEDURE_STATUS = gql`
  mutation updateProcedureStatus($input: UpdateProcedureStatusInput!) {
    updateProcedureStatus(input: $input) {
      procedureStatus {
        id
      }
    }
  }
`

interface UpdateProcedureStatusQueryVariables {
  input: {
    id: string
    initialized?: boolean
  }
}

const R9_REPORT = gql`
  query R9Report($procedureStatusId: ID!) {
    client {
      id
      procedureStatus(procedureStatusId: $procedureStatusId) {
        id
        procedureType
        employee {
          id
          staffCode
          displayFullName
          employeeDependents {
            id
            firstName
            lastName
            dependentSocialInsurance
          }
        }
        r9ParamSet {
          id
          employeeSelected
          canNotUseMyNumberInsurance
          notes
          isUseMyNumber
          needLaborConsultant
          laborConsultantCreateDate
          laborConsultantName
          laborConsultantAgentType
          laborConsultantPrefecture {
            id
          }
          laborConsultantPhoneNumber0
          laborConsultantPhoneNumber1
          laborConsultantPhoneNumber2
          version
          needUpgrade
          r9DependentParamSets {
            employeeDependentId
          }
        }
      }
    }
  }
`

interface R9ReportQueryResult {
  client: {
    id: string
    procedureStatus: {
      id: string
      procedureType: string
      employee: {
        id: string
        staffCode: string
        displayFullName: string
        employeeDependents: {
          id: string
          firstName: string
          lastName: string
          dependentSocialInsurance: boolean
        }[]
      }
      r9ParamSet: {
        id?: string
        employeeSelected: boolean
        canNotUseMyNumberInsurance: boolean
        notes?: string
        isUseMyNumber: boolean
        needLaborConsultant: boolean
        laborConsultantCreateDate?: string
        laborConsultantName?: string
        laborConsultantAgentType?: 'submission_agent' | 'office_agent'
        laborConsultantPrefecture?: {
          id: string
        }
        laborConsultantPhoneNumber0?: string
        laborConsultantPhoneNumber1?: string
        laborConsultantPhoneNumber2?: string
        version: number
        needUpgrade: boolean
        r9DependentParamSets?: {
          employeeDependentId: string
        }[]
      }
    }
  }
}

interface R9ReportQueryVariables {
  procedureStatusId: string
}

const NOTES_MAX_LINES = 3

const schema = yup.object({
  employeeSelected: yup.boolean().notRequired(),
  canNotUseMyNumberInsurance: yup.boolean().notRequired().label('マイナ保険証を利用できない'),
  notes: yup
    .string()
    .notRequired()
    .max(100)
    .test(
      'max-lines',
      `備考は${NOTES_MAX_LINES}行以内で入力してください。`,
      (value) => !value || value.split(/\r\n|\r|\n/).length <= NOTES_MAX_LINES
    )
    .label('備考'),
  isUseMyNumber: yup.boolean().notRequired().label('マイナンバーを出力する'),
  laborConsultantForm: laborConsultantSchema.required(),
  r9DependentParamSets: yup.array().of(yup.string().required()).required().default([]),
})

const makeDefaultValues = (ps: R9ReportQueryResult['client']['procedureStatus']): Schema => {
  const r9 = ps.r9ParamSet
  return {
    employeeSelected: r9.employeeSelected,
    canNotUseMyNumberInsurance: r9.canNotUseMyNumberInsurance,
    notes: r9.notes,
    isUseMyNumber: r9.isUseMyNumber,
    laborConsultantForm: {
      needLaborConsultant: r9.needLaborConsultant,
      laborConsultantCreateDate: r9.laborConsultantCreateDate
        ? dateFormat(r9.laborConsultantCreateDate, 'YYYY/MM/DD')
        : '',
      laborConsultantName: r9.laborConsultantName || '',
      laborConsultantAgentType: r9.laborConsultantAgentType || undefined,
      laborConsultantPrefectureId: r9.laborConsultantPrefecture?.id || undefined,
      laborConsultantPhoneNumber0: r9.laborConsultantPhoneNumber0 || '',
      laborConsultantPhoneNumber1: r9.laborConsultantPhoneNumber1 || '',
      laborConsultantPhoneNumber2: r9.laborConsultantPhoneNumber2 || '',
    },
    r9DependentParamSets: r9.r9DependentParamSets?.map((d) => d.employeeDependentId) || [],
  }
}

type Schema = yup.InferType<typeof schema>

interface FormProps {
  procedureStatus: R9ReportQueryResult['client']['procedureStatus']
  submitText: string
  onSubmit: SubmitHandler<Schema>
  procedureContext: ProcedureContext
  comments?: ReactNode
  renderReject?: ReportFormProps['renderReject']
}

const Form: FC<FormProps> = ({procedureStatus, submitText, onSubmit, procedureContext, comments, renderReject}) => {
  const methods = useForm<Schema>({
    defaultValues: makeDefaultValues(procedureStatus),
    resolver: yupResolver(schema),
    mode: 'onTouched',
    shouldFocusError: false,
    context: {procedureContext},
  })
  const {
    register,
    control,
    handleSubmit,
    formState: {errors, isSubmitting},
  } = methods
  const handleSubmitError = () => {
    window.scrollTo({top: 0, behavior: 'smooth'})
  }
  const {confirmUpgrade, confirmUpgradeModal} = useConfirmUpgrade(
    procedureStatus.r9ParamSet.needUpgrade,
    handleSubmit(onSubmit, handleSubmitError)
  )
  const dependents = procedureStatus.employee.employeeDependents.filter(
    (dependent) => dependent.dependentSocialInsurance
  )

  return (
    <FormProvider {...methods}>
      <div className="basic_information l-wrap-xs">
        <FormErrors errors={errors} isSubmitting={isSubmitting} />
        {comments}
        <Panel>
          <Head>健康保険 資格確認書交付申請書</Head>
          <Body className={styles.formBody}>
            <Panel>
              <Head>交付対象者</Head>
              <Body className={styles.formBody}>
                <div className={styles.field}>
                  <Label>被保険者</Label>
                  <div className={styles.indent}>
                    <Checkbox {...register('employeeSelected')}>{procedureStatus.employee.displayFullName}</Checkbox>
                  </div>
                </div>
                {dependents && dependents.length > 0 && (
                  <div className={styles.field}>
                    <Label>被扶養者</Label>
                    <Controller
                      control={control}
                      name="r9DependentParamSets"
                      render={({field: {value = [], onChange, onBlur}}) => (
                        <>
                          {dependents.map((dependent) => {
                            const isChecked = value.includes(dependent.id)

                            return (
                              <div className={styles.indent} key={dependent.id}>
                                <Checkbox
                                  checked={isChecked}
                                  onBlur={onBlur}
                                  onChange={(event) => {
                                    const nextValue = event.target.checked
                                      ? [...value, dependent.id]
                                      : value.filter((selectedDependentId) => selectedDependentId !== dependent.id)

                                    onChange(nextValue)
                                  }}
                                >
                                  {dependent.lastName} {dependent.firstName}
                                </Checkbox>
                              </div>
                            )
                          })}
                        </>
                      )}
                    />
                  </div>
                )}
              </Body>
            </Panel>
            <Panel>
              <Head>交付理由</Head>
              <Body className={styles.formBody}>
                <Label>※該当する場合はチェックを入れてください。</Label>
                <Checkbox {...register('canNotUseMyNumberInsurance')}>
                  以下のケースに該当するような、マイナ保険証を利用できない状況にあるため
                </Checkbox>
                <Label>（例）</Label>
                <div className={styles.indent}>
                  ・マイナンバーカードを所持していない
                  <br />
                  ・マイナ保険証の利用登録をしていない
                  <br />
                  ・家族や介助者等が同行して資格確認を補助する必要がある
                  <br />
                  ・その他事由による
                </div>
              </Body>
            </Panel>
            <Panel>
              <Head>備考</Head>
              <Body className={styles.formBody}>
                <Label>備考</Label>
                <Textarea rows={5} {...register('notes')} isError={!!errors.notes} />
                {errors.notes?.message && <ErrorMessage>{errors.notes.message}</ErrorMessage>}
              </Body>
            </Panel>
            <Checkbox {...register('isUseMyNumber')}>マイナンバーを出力する</Checkbox>
            <div>※記号・番号を登録されている場合はマイナンバーは記載不要です。</div>
            <LaborConsultantSection
              procedureStatusId={procedureStatus.id}
              reportName="R9"
              prefix="laborConsultantForm."
            />
          </Body>
        </Panel>

        <div className="u-ta-c u-mt30">
          {renderReject?.({isSubmitting})}
          <Button primary onClick={confirmUpgrade} disabled={isSubmitting}>
            {submitText}
          </Button>
        </div>
      </div>

      {confirmUpgradeModal}
    </FormProvider>
  )
}

export const R9Report: FC<ReportFormProps> = ({
  procedureStatusId,
  submitText,
  afterSubmit,
  procedureContext,
  comments,
  renderReject,
}) => {
  const dispatch = useDispatch()
  const {data, loading} = useQuery<R9ReportQueryResult, R9ReportQueryVariables>(R9_REPORT, {
    variables: {
      procedureStatusId,
    },
    fetchPolicy: 'network-only',
  })
  const procedureStatus = data?.client?.procedureStatus

  const [updateR9ParamSet] = useMutation<unknown, UpdateR9ParamSetQueryVariables>(UPDATE_R9_PARAM_SET)
  const [updateProcedureStatus] = useMutation<unknown, UpdateProcedureStatusQueryVariables>(UPDATE_PROCEDURE_STATUS)

  if (loading || !procedureStatus) {
    return null
  }

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const blankToNull = (value: string | undefined | null) => (value ? value : null)

    try {
      await updateR9ParamSet({
        variables: {
          input: {
            procedureStatusId,
            attributes: {
              employeeSelected: data.employeeSelected,
              canNotUseMyNumberInsurance: data.canNotUseMyNumberInsurance,
              notes: data.notes,
              isUseMyNumber: data.isUseMyNumber,
              needLaborConsultant: data.laborConsultantForm.needLaborConsultant,
              laborConsultantCreateDate: blankToNull(data.laborConsultantForm.laborConsultantCreateDate),
              laborConsultantName: data.laborConsultantForm.laborConsultantName,
              laborConsultantAgentType: data.laborConsultantForm.laborConsultantAgentType,
              laborConsultantPrefectureId: data.laborConsultantForm.laborConsultantPrefectureId,
              laborConsultantPhoneNumber0: data.laborConsultantForm.laborConsultantPhoneNumber0,
              laborConsultantPhoneNumber1: data.laborConsultantForm.laborConsultantPhoneNumber1,
              laborConsultantPhoneNumber2: data.laborConsultantForm.laborConsultantPhoneNumber2,
              version: procedureStatus.r9ParamSet.version,
              r9DependentParamSets: data.r9DependentParamSets.map((employeeDependentId) => ({
                employeeDependentId,
              })),
            },
          },
        },
      })
      await updateProcedureStatus({
        variables: {
          input: {
            id: procedureStatusId,
            initialized: true,
          },
        },
      })
    } catch (err) {
      dispatch(asyncError(err))
      return
    }

    return await afterSubmit()
  }

  return (
    <>
      <div className="l-title-wrap">
        <h1 className="m-title-main">{titleMap[procedureStatus.procedureType]}</h1>
        <EmployeeName employee={procedureStatus.employee} />
      </div>
      <Form
        procedureStatus={procedureStatus}
        submitText={submitText}
        onSubmit={onSubmit}
        procedureContext={procedureContext}
        renderReject={renderReject}
        comments={comments}
      />
    </>
  )
}
